import {
	Component, Input, Output, EventEmitter, SimpleChanges, ViewChild, 
	 ChangeDetectorRef, OnInit, OnChanges, OnDestroy, ElementRef, ChangeDetectionStrategy
} from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { DiffType, DiffSpan, DiffSpans, DiffResult } from 'DiscoveryEngine.TS/CommandLine';
import { DiffFileComponent } from '../diff-file/diff-file.component';
import { DiscResultsService } from 'discovery';
import { IdentityService } from 'identity';
import { NavigationService } from 'netview';
import {
	InfoSetView, InfoSetViewColumn, InfoSetViewEntry, InfoSetViewValue,
	DisplayDeviceListEntryDto, DeviceDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { InfoSetNames } from 'DiscoveryEngine.TS/InfoSets';
import { DateService, MessageService } from 'base';
import { StringUtils } from 'DiscoveryEngine.TS/Utils';


class ConfigEntry {
	public index: number = -1;
	public configKey: string = "";
	public lastCapture: string = "";
	public configText: string = "";
}


@Component({
    selector: 'diff-viewer',
	templateUrl: './diff-viewer.component.html',
	styleUrls: ['./diff-viewer.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DiffViewerComponent implements OnInit, OnChanges, OnDestroy {

	@Input()
	public visible: boolean;
	@Output()
	public visibleChange: EventEmitter<boolean>;

	@Input()
	public deviceGuid: string;

	@Input()
	public deviceName: string;

	@Input()
	public infoSetView: InfoSetView;

	@Input()
	public configKey: string;

	@Input()
	public configLastCapture: string;

	@Input()
	public configText: string;

	@Output()
	public onClose: EventEmitter<any>;

	@ViewChild('contentDiv', { static: true })
	public contentDiv: ElementRef<HTMLDivElement>;

	@ViewChild('contentHeaderDiv', { static: true })
	public contentHeaderDiv: ElementRef<HTMLDivElement>;

	@ViewChild('leftFile', { static: true })
	public leftFile: DiffFileComponent;

	@ViewChild('rightFile', { static: true })
	public rightFile: DiffFileComponent;

	public leftDeviceGuid: string;
	public leftDeviceName: string;
	public leftInfoSetView: InfoSetView;
	public leftConfigKey: string;
	public leftConfigLastCapture: string;
	public leftConfigText: string;
	public leftConfigEntries: ConfigEntry[];

	public rightDeviceGuid: string;
	public rightDeviceName: string;
	public rightInfoSetView: InfoSetView;
	public rightConfigKey: string;
	public rightConfigLastCapture: string;
	public rightConfigText: string;
	public rightConfigEntries: ConfigEntry[];

	public diffResult: DiffResult;
	public topCoord: number;
	public leftCoord: number;
	public curDiffIndex: number;
	public diffFileHeight: number;
	public diffFileWidth: number;

	private timerId: any;

	public displayDeviceSelector: boolean;
	public deviceSelectorSide: number;	// 1 => left, 2 => right


	constructor(private discResultsService: DiscResultsService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private dateService: DateService,
		private messageService: MessageService,
		private changeDetector: ChangeDetectorRef) {

		this.visible = false;
		this.visibleChange = new EventEmitter<boolean>();
		this.onClose = new EventEmitter<any>();
		this.deviceGuid = "";
		this.deviceName = "";
		this.infoSetView = null;
		this.configKey = "";
		this.configLastCapture = "";
		this.configText = "";

		this.leftDeviceGuid = "";
		this.leftDeviceName = "";
		this.leftInfoSetView = null;
		this.leftConfigKey = "";
		this.leftConfigLastCapture = "";
		this.leftConfigText = "";

		this.rightDeviceGuid = "";
		this.rightDeviceName = "";
		this.rightInfoSetView = null;
		this.rightConfigKey = "";
		this.rightConfigLastCapture = "";
		this.rightConfigText = "";

		this.diffResult = new DiffResult();
		this.topCoord = 0;
		this.leftCoord = 0;
		this.curDiffIndex = 0;
		this.diffFileHeight = 0;
		this.diffFileWidth = 0;

		this.timerId = undefined;

		this.displayDeviceSelector = false;
		this.deviceSelectorSide = 0;
	}

	public formatDateHms(date: string | Date): string {
		return this.dateService.formatDateHms(date);
	}

	private rebuildLeftConfigEntries(): void {

		this.leftConfigEntries = [];
		this.rebuildConfigEntries(this.leftConfigEntries, this.leftInfoSetView);
	}

	private rebuildRightConfigEntries(): void {

		this.rightConfigEntries = [];
		this.rebuildConfigEntries(this.rightConfigEntries, this.rightInfoSetView);
	}

	private readonly ObfuscateConfigs = false;

	private rebuildConfigEntries(configEntries: ConfigEntry[], infoSetView: InfoSetView): void {

		if (infoSetView) {
			for (let i = 0; i < infoSetView.entries.length; ++i) {

				let viewEntry: InfoSetViewEntry = infoSetView.entries[i];

				let configEntry = new ConfigEntry();

				configEntry.index = i;
				configEntry.configKey = viewEntry.values[0].value;
				configEntry.lastCapture = viewEntry.values[2].value;
				configEntry.configText = (this.ObfuscateConfigs ? 
					StringUtils.ClarifyString2(viewEntry.values[3].value) : viewEntry.values[3].value);

				configEntries.push(configEntry);
			}
		}
	}

	public onSelectDevice(side: number): void {

		this.deviceSelectorSide = side;
		this.displayDeviceSelector = true;
	}

	public onDeviceSelectorCancel(): void {

		this.displayDeviceSelector = false;
		this.deviceSelectorSide = 0;
	}

	private findInfoSetView(device: DeviceDto, infoSetName: string): InfoSetView {
		if (device) {
			for (let i = 0; i < device.infoSets.length; ++i) {
				let infoSet = device.infoSets[i];
				if (infoSet.infoSetName === infoSetName) {
					return infoSet;
				}
			}
		}
		return null;
	}

	public onDeviceSelectorOk(devices: DisplayDeviceListEntryDto[]): void {

		let side = this.deviceSelectorSide;
		let deviceGuid = devices[0].deviceGuid;

		this.displayDeviceSelector = false;
		this.deviceSelectorSide = 0;
		this.curDiffIndex = 0;

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.discResultsService.getDeviceView(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, deviceGuid).subscribe(
				(device: DeviceDto) => {

					let configInfoSetView: InfoSetView = this.findInfoSetView(device, InfoSetNames.ConfigInfo);

					switch (side) {
						case 1:
							this.leftDeviceGuid = device.deviceGuid;
							this.leftDeviceName = device.displayName;

							if (configInfoSetView && configInfoSetView.entries.length > 0) {
								this.leftInfoSetView = configInfoSetView;

								let viewEntry: InfoSetViewEntry = configInfoSetView.entries[0];
								this.leftConfigKey = viewEntry.values[0].value;
								this.leftConfigLastCapture = viewEntry.values[2].value;
								this.leftConfigText = (this.ObfuscateConfigs ? 
									StringUtils.ClarifyString2(viewEntry.values[3].value) : viewEntry.values[3].value);
							}
							else {
								this.leftInfoSetView = null;
								this.leftConfigKey = "";
								this.leftConfigLastCapture = "";
								this.leftConfigText = "";
							}

							this.rebuildLeftConfigEntries();
							this.runDiff();

							break;
						case 2:
							this.rightDeviceGuid = device.deviceGuid;
							this.rightDeviceName = device.displayName;

							if (configInfoSetView && configInfoSetView.entries.length > 0) {
								this.rightInfoSetView = configInfoSetView;

								let viewEntry: InfoSetViewEntry = configInfoSetView.entries[0];
								this.rightConfigKey = viewEntry.values[0].value;
								this.rightConfigLastCapture = viewEntry.values[2].value;
								this.rightConfigText = (this.ObfuscateConfigs ? 
									StringUtils.ClarifyString2(viewEntry.values[3].value) : viewEntry.values[3].value);
							}
							else {
								this.rightInfoSetView = null;
								this.rightConfigKey = "";
								this.rightConfigLastCapture = "";
								this.rightConfigText = "";
							}

							this.rebuildRightConfigEntries();
							this.runDiff();

							break;
						default:
							console.assert(false);
							break;
					}
					
				});
	}

	public onUpdateLeftConfig(event): void {

		let index: number = event.target.value;
		let selectedConfigEntry: ConfigEntry = this.leftConfigEntries[index];

		this.leftConfigKey = selectedConfigEntry.configKey;
		this.leftConfigLastCapture = selectedConfigEntry.lastCapture;
		this.leftConfigText = selectedConfigEntry.configText;

		this.runDiff();
	}

	public onUpdateRightConfig(event): void {

		let index: number = event.target.value;
		let selectedConfigEntry: ConfigEntry = this.rightConfigEntries[index];

		this.rightConfigKey = selectedConfigEntry.configKey;
		this.rightConfigLastCapture = selectedConfigEntry.lastCapture;
		this.rightConfigText = selectedConfigEntry.configText;

		this.runDiff();
	}

	private runDiff(): void {

		this.diffResult = new DiffResult();
		this.topCoord = 0;
		this.leftCoord = 0;

		if (!!this.leftInfoSetView && !!this.rightInfoSetView) {

			let sessionGuid = this.identityService.sessionGuid;
			let netViewGuid = this.navService.location.netViewGuid;
			let agentGuid = this.navService.location.agentGuid;
			let schedDiscGuid = this.navService.location.schedDiscGuid;
			let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

			this.messageService.startWork();
			let leftText = (this.ObfuscateConfigs ? 
				StringUtils.ObfuscateString2(this.leftConfigText) : this.leftConfigText);
			let rightText = (this.ObfuscateConfigs ? 
				StringUtils.ObfuscateString2(this.rightConfigText) : this.rightConfigText);
			this.discResultsService.diff(sessionGuid, netViewGuid, agentGuid, schedDiscGuid, schedDiscRsltGuid,
				leftText, rightText).subscribe(
					(result: DiffResult) => {
						if (this.ObfuscateConfigs) {
							StringUtils.ClarifyStrings2(result.leftSpans.lines);
							StringUtils.ClarifyStrings2(result.rightSpans.lines);
						}
						this.diffResult = result;
						this.propagateDiffResultChanges();
						this.messageService.endWork();
					},
					(error) => {
						this.propagateDiffResultChanges();
						this.messageService.endWork();
					}
				);
		}
		else {
			this.propagateDiffResultChanges();
		}
	}

	private propagateDiffResultChanges(): void {
		// First, propagate changes to embedded <diff-file> components
		this.changeDetector.detectChanges();
		// Then, propagate changes from embedded <diff-file> components to this component
		this.changeDetector.markForCheck();
	}

	public show(): void {
		this.visible = true;
		this.visibleChange.emit(true);
	}

	public hide(): void {
		this.visible = false;
		this.visibleChange.emit(false);
	}

	public onDialogHide(): void {
		this.hide();
		this.onClose.emit();
	}

	public onCloseClicked(): void {
		this.hide();
		this.onClose.emit();
	}

	public ngOnInit(): void {

	}

	private setDimensions(): void {

		let newHeight = this.contentDiv.nativeElement.clientHeight - this.contentHeaderDiv.nativeElement.clientHeight - 5;
		if (this.diffFileHeight != newHeight) {
			this.diffFileHeight = newHeight;
			this.changeDetector.markForCheck();
		}

		let newWidth = this.contentDiv.nativeElement.clientWidth / 2;
		if (this.diffFileWidth != newWidth) {
			this.diffFileWidth = newWidth;
			this.changeDetector.markForCheck();
		}
	}

	public ngOnDestroy(): void {

		this.stopTimer();
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (changes["visible"]) {
			if (this.visible) {

				this.leftDeviceGuid = this.deviceGuid;
				this.leftDeviceName = this.deviceName;
				this.leftInfoSetView = this.infoSetView;
				this.leftConfigKey = this.configKey;
				this.leftConfigLastCapture = this.configLastCapture;
				this.leftConfigText = this.configText;

				this.rightDeviceGuid = this.deviceGuid;
				this.rightDeviceName = this.deviceName;
				this.rightInfoSetView = this.infoSetView;
				this.rightConfigKey = this.configKey;
				this.rightConfigLastCapture = this.configLastCapture;
				this.rightConfigText = this.configText;
				// Select another config for the right side, if there is one
				for (let i = 0; i < this.rightInfoSetView.entries.length; ++i) {

					let viewEntry: InfoSetViewEntry = this.rightInfoSetView.entries[i];

					let configKey = viewEntry.values[0].value;
					let lastCapture = viewEntry.values[2].value;
					let configText = (this.ObfuscateConfigs ? 
						StringUtils.ClarifyString2(viewEntry.values[3].value) : viewEntry.values[3].value);

					if ((configKey != this.leftConfigKey || lastCapture != this.leftConfigLastCapture)) {

						this.rightConfigKey = configKey;
						this.rightConfigLastCapture = lastCapture;
						this.rightConfigText = configText;

						break;
					}
				}

				this.diffResult = new DiffResult();
				this.topCoord = 0;
				this.leftCoord = 0;
				this.curDiffIndex = 0;

				const InitialDiffFileHeight = 500;
				const InitialDiffFileWidth = 450;
				this.diffFileHeight = InitialDiffFileHeight;
				this.diffFileWidth = InitialDiffFileWidth;

				this.rebuildLeftConfigEntries();
				this.rebuildRightConfigEntries();

				setTimeout(() => this.runDiff(), 0);

				this.startTimer();
			}
			else {
				this.stopTimer();
			}
		}
	}

	private startTimer(): void {
		if (this.timerId === undefined) {
			this.timerId = setInterval(() => {
				this.setDimensions();
			}, 10);
		}
	}

	private stopTimer(): void {
		if (this.timerId !== undefined) {
			clearInterval(this.timerId);
			this.timerId = undefined;
		}
	}

	public get enablePrevDiff(): boolean {
		return this.leftFile.enablePrevDiff;
	}

	public get enableNextDiff(): boolean {
		return this.leftFile.enableNextDiff;
	}

	public prevDiff(): void {
		this.disableScrollEvents();
		this.leftFile.prevDiff();
		this.enableScrollEvents();
	}

	public nextDiff(): void {
		this.disableScrollEvents();
		this.leftFile.nextDiff();
		this.enableScrollEvents();
	}

	private disableScrollEvents(): void {
		this.leftFile.ignoreScrollEvents = true;
		this.rightFile.ignoreScrollEvents = true;
	}

	private enableScrollEvents(): void {
		setTimeout(() => {
			this.leftFile.ignoreScrollEvents = false;
			this.rightFile.ignoreScrollEvents = false;
		}, 0);
	}

}
